<template>
  <v-dialog v-model="display" class="app-dialog-datetime" :width="dialogWidth" @click:outside="handleOnClearDateTime">
    <template v-slot:activator="{ on }">
      <Input
        v-bind="textFieldProps"
        :outlined="true"
        :active="true"
        :disabled="disabled"
        :loading="loading"
        :label="label"
        :value="formattedDatetime"
        :placeholder="placeholder"
        :tabindex="tabindex"
        v-on="on"
        readonly
        clearable
        @click:clear="handleOnClearDateTime"
      />
    </template>

    <v-card class="date-time-card">
      <v-card-text class="date-time-card-body">
        <v-date-picker :scrollable="true" color="primary" v-model="date" v-bind="datePickerProps" :min="nowDate" />
        <v-time-picker
          :scrollable="true"
          ref="timer"
          class="v-time-picker-custom"
          v-model="time"
          v-bind="timePickerProps"
          color="primary"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <slot name="actions" :parent="this">
          <v-btn outlined color="dark" text @click.native="handleOnResetDateTime">{{ $t("cancel") }}</v-btn>
          <Button outlined color="primary" @click="handleOnApplyDateTime" :title="$t('apply')" />
        </slot>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { format, parse } from "date-fns";
import moment from "moment-timezone/moment-timezone-utils";

const DEFAULT_DATE = "";
const DEFAULT_TIME = "";
const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
const DEFAULT_TIME_FORMAT = "HH:mm:ss";
const DEFAULT_DIALOG_WIDTH = "77rem";

export default {
  name: "v-datetime-picker",

  model: {
    prop: "datetime",
    event: "input",
  },

  data() {
    return {
      display: false,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
      initDate: DEFAULT_DATE,
      initTime: DEFAULT_TIME,
    };
  },

  props: {
    attach: {
      type: String,
      default: null,
    },
    tabindex: {
      type: [String, Number],
      default: 1,
    },
    datetime: {
      type: [Date, String],
      default: null,
    },
    disabled: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    label: {
      type: String,
      default: "",
    },
    dialogWidth: {
      type: [String, Number],
      default: DEFAULT_DIALOG_WIDTH,
    },
    dateFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT,
    },
    timeFormat: {
      type: String,
      default: "HH:mm",
    },
    textFieldProps: {
      type: Object,
    },
    datePickerProps: {
      type: Object,
    },
    timePickerProps: {
      type: Object,
    },
    placeholder: {
      type: String,
    },
  },

  watch: {
    display: {
      immediate: true,
      handler(display) {
        if (display) {
          this.initTime = _.cloneDeep(this.time);
          this.initDate = _.cloneDeep(this.date);

          if (!this.date) {
            this.date = window.moment().format("YYYY-MM-DD");
          }

          if (!this.time) {
            this.time = window.moment().add(30, "minute").format("HH:mm");
          }
        }
      },
    },
  },

  mounted() {
    this.init();
  },

  computed: {
    nowDate() {
      return moment(new Date()).format("YYYY-MM-DD");
    },

    nowTime() {
      return moment(new Date()).format("HH:mm");
    },

    dateTimeFormat() {
      return this.dateFormat + " " + this.timeFormat;
    },

    defaultDateTimeFormat() {
      return DEFAULT_DATE_FORMAT + " " + DEFAULT_TIME_FORMAT;
    },

    formattedDatetime() {
      return this.selectedDatetime && this.selectedDatetime !== "Invalid Date"
        ? format(this.selectedDatetime, this.dateTimeFormat)
        : "";
    },

    selectedDatetime() {
      if (this.date && this.time) {
        let datetimeString = this.date + " " + this.time;
        if (this.time.length === 5) {
          datetimeString += ":00";
        }
        return parse(datetimeString, this.defaultDateTimeFormat, new Date());
      } else {
        return null;
      }
    },

    dateSelected() {
      return !this.date;
    },
  },

  methods: {
    /**
     * Handle on init date time component
     * @return {void}
     */
    init() {
      if (!this.datetime) {
        return;
      }

      // let initDateTime;
      // if (this.datetime instanceof Date) {
      //   initDateTime = this.datetime;
      // } else if (typeof this.datetime === "string" || this.datetime instanceof String) {
      //   // see https://stackoverflow.com/a/9436948
      //   initDateTime = parse(this.datetime, this.dateTimeFormat, new Date());
      // }

      // this.date = this.initDate = format(initDateTime, DEFAULT_DATE_FORMAT);
      // this.time = this.initTime = format(initDateTime, DEFAULT_TIME_FORMAT);

      this.time = window.moment(this.datetime).format("HH:mm:ss");
      this.date = window.moment(this.datetime).format("YYYY-MM-DD");
    },

    /**
     * Handle on apply date time
     * @return {Promise<any>}
     */
    handleOnApplyDateTime() {
      // check the datetime is before now
      const current = moment();
      const selected = moment(this.selectedDatetime);
      if (current.diff(selected) > 0) {
        return this.$confirm(this.$t("datetime_error_message"), {
          buttonTrueColor: "primary",
          title: this.$t("datetime_error_title"),
          buttonTrueText: this.$t("datetime_error_button_title"),
          buttonFalseText: "",
        });
      }
      this.handleOnResetPicker();
      this.$emit("input", this.selectedDatetime);
      this.$emit("change", this.selectedDatetime);
    },

    /***
     * Handle on clear date time
     * @return {void}
     */
    handleOnClearDateTime() {
      this.handleOnResetPicker();
      this.time = null;
      this.date = null;
      this.$emit("input", this.selectedDatetime);
      this.$emit("change", this.selectedDatetime);
    },

    /**
     * Handle on reset date time values
     * @return {void}
     */
    handleOnResetDateTime() {
      this.handleOnResetPicker();
      this.time = _.cloneDeep(this.initTime);
      this.date = _.cloneDeep(this.initDate);
    },

    /***
     * Handle on reset date time picker modal
     * @return {void}
     */
    handleOnResetPicker() {
      this.display = false;
      if (this.$refs.timer) {
        this.$refs.timer.selectingHour = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./styles.scss" />
